import React from 'react';
import { Link } from 'react-router-dom';
import {
  StyledPostTitle,
  StyledParagraph,
  StyledSectionHeading,
  StyledOL,
} from '../styled';

const thStyle = {
  padding: '8px 12px 8px 0',
  borderBottom: '1px solid #000',
  verticalAlign: 'bottom',
};
const thLastStyle = { ...thStyle, padding: '8px 0' };
const rowHeadStyle = {
  padding: '10px 12px 10px 0',
  verticalAlign: 'top',
  fontWeight: 500,
};
const tdStyle = { padding: '10px 12px 10px 0', verticalAlign: 'top' };
const tdLastStyle = { padding: '10px 0', verticalAlign: 'top' };

const COMPARISON_ROWS = [
  [
    'Made by',
    'Nordeus (part of Take-Two since 2021)',
    'Squiggly Games',
  ],
  [
    'Where you play',
    'Mostly a mobile app (iOS, Android)',
    'In the browser (phone or desktop) or on Steam',
  ],
  [
    'League size',
    '14 clubs per league',
    'Servers of 30 clubs, split into three leagues of ten',
  ],
  ['Season length', '28 days', '3 weeks, then a 3-day off-season'],
  [
    'Match rhythm',
    'One league match a day',
    'A league match every night at 7 pm server time, cup games at noon on most days',
  ],
  [
    'Transfers',
    'Auctions against other managers, and negotiations that cost tokens',
    'Agree a deal with the other manager, then send the offer in the portal',
  ],
  [
    'Where the social side happens',
    'In the app',
    'In your league’s Discord',
  ],
  [
    'Price',
    'Free, with tokens and other in-app purchases',
    'Free to play, optional paid Patron extras',
  ],
];

export default function Article() {
  return (
    <>
      <StyledPostTitle id="top-eleven-alternative">
        Top Eleven alternative: real managers, a league in Discord
      </StyledPostTitle>
      <StyledParagraph big>
        Top Eleven is one of the best-known football manager games on phones.
        Nordeus, a studio in Belgrade, launched it on Facebook before moving
        it to mobile. People who search for a Top Eleven
        alternative usually still like managing a club on their phone. What
        they want is a different feel: rivals they actually know, a season that
        ends sooner, and transfers you negotiate with other managers.
      </StyledParagraph>
      <StyledParagraph big>
        The Open League (TOL) is a free football manager you play in the
        browser or on Steam. Clubs are run by real people (a club with no
        manager is run by a bot until someone claims it), seasons last three weeks, a league match is played every night at 7 pm,
        and the league lives in Discord. It is smaller and simpler than Top
        Eleven, and this page is honest about that.
      </StyledParagraph>

      <StyledSectionHeading>
        What Top Eleven does well, and why people look elsewhere
      </StyledSectionHeading>
      <StyledParagraph big>
        Top Eleven earned its audience. It is quick to pick up, there is a match
        every day, and when Take-Two bought Nordeus in June 2021 it said the
        game had over 240 million registered users. You can play it for free for as long
        as you like.
      </StyledParagraph>
      <StyledParagraph big>
        The same design explains why some managers drift away:
      </StyledParagraph>
      <StyledParagraph big>
        <StyledOL>
          <li>
            Your rivals are strangers. Each league has 14 clubs drawn from a
            huge global player base, so you rarely know who is across the table.
          </li>
          <li>
            The season is long. A season runs 28 days, with a league match every
            day after a two-day preseason.
          </li>
        </StyledOL>
      </StyledParagraph>
      <StyledParagraph big>
        If none of that bothers you, keep playing Top Eleven. If it does, read
        on.
      </StyledParagraph>

      <StyledSectionHeading>
        Top Eleven vs The Open League at a glance
      </StyledSectionHeading>
      <StyledParagraph small>Facts as of October 2026.</StyledParagraph>
      <div style={{ overflowX: 'auto', marginBottom: 20 }}>
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontFamily: 'Archivo, sans-serif',
            fontWeight: 300,
            fontSize: 16,
            lineHeight: '26px',
            letterSpacing: '0.4px',
            textAlign: 'left',
          }}
        >
          <thead>
            <tr>
              <th style={thStyle} aria-label="Comparison" />
              <th style={thStyle}>Top Eleven</th>
              <th style={thLastStyle}>The Open League</th>
            </tr>
          </thead>
          <tbody>
            {COMPARISON_ROWS.map(([label, topEleven, tol]) => (
              <tr key={label}>
                <th scope="row" style={rowHeadStyle}>
                  {label}
                </th>
                <td style={tdStyle}>{topEleven}</td>
                <td style={tdLastStyle}>{tol}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <StyledSectionHeading>
        Where The Open League is the better fit
      </StyledSectionHeading>
      <StyledParagraph big>
        <StyledOL>
          <li>
            You want to know your rivals. Each server has 30 clubs, almost all
            run by real people, so you learn who is above and below you in the table.
          </li>
          <li>
            You like negotiating. Transfers start as a chat with another
            manager, then you make the offer official in the club portal. No
            in-game currency is involved.
          </li>
          <li>
            You want a season with an ending. Three weeks of nightly league
            matches, promotion and relegation, then a short{' '}
            <Link to="/user-guide/off-season">off-season</Link> with a youth
            camp before the next season starts.
          </li>
          <li>
            Your group already lives in Discord. Match highlights, banter and
            deals all happen there. If you are bringing friends, see our guide
            to playing a{' '}
            <Link to="/blog/football-manager-with-friends">
              football manager with friends
            </Link>
            , and for how the league runs inside Discord, see{' '}
            <Link to="/blog/discord-football-manager">
              Discord football manager
            </Link>
            .
          </li>
          <li>
            You still want to play on your phone. TOL runs in the mobile
            browser, with nothing to install. See{' '}
            <Link to="/blog/play-football-manager-on-phone">
              play football manager on phone
            </Link>
            .
          </li>
        </StyledOL>
      </StyledParagraph>

      <StyledSectionHeading>
        Where Top Eleven is still the better pick
      </StyledSectionHeading>
      <StyledParagraph big>
        <StyledOL>
          <li>
            Polish and scale. Top Eleven has a big studio, a polished app and a
            huge player base. TOL is a small game with 30-club servers.
          </li>
          <li>
            More control over match times. Top Eleven sets your match times for
            you, but you can move a home match to a slot that suits you (it
            costs 2 tokens). In TOL, every club on a server plays at 7 pm, so
            you choose a server whose match time fits your evenings.
          </li>
          <li>
            Lots of events and modes. Top Eleven runs extra competitions and
            live events. TOL keeps things simple: league, cups and the transfer
            market.
          </li>
        </StyledOL>
      </StyledParagraph>
      <StyledParagraph big>
        Plenty of managers will be happy playing both.
      </StyledParagraph>

      <StyledSectionHeading>
        If Top Eleven is not the game you are comparing
      </StyledSectionHeading>
      <StyledParagraph big>
        If your real comparison is Football Manager, read our{' '}
        <Link to="/blog/free-football-manager-alternative">
          free football manager alternative
        </Link>{' '}
        guide. If you came from the long-running browser game Hattrick, see our{' '}
        <Link to="/blog/hattrick-alternative">Hattrick alternative</Link> page.
      </StyledParagraph>

      <StyledSectionHeading>How to start a season tonight</StyledSectionHeading>
      <StyledParagraph big>
        <StyledOL>
          <li>
            Open <Link to="/">The Open League</Link> in your browser, or find it
            on Steam, and register or log in.
          </li>
          <li>
            Pick a server whose 7 pm match time suits your evenings, and claim a
            club.
          </li>
          <li>
            Set your starting eleven and tactics, then join Discord to follow
            the match.
          </li>
          <li>
            Read <Link to="/user-guide/start">Getting started</Link> and the{' '}
            <Link to="/user-guide/trading">trading guide</Link> before your
            first deal.
          </li>
        </StyledOL>
      </StyledParagraph>

      <StyledSectionHeading>FAQ</StyledSectionHeading>
      <StyledParagraph big>
        <strong>Is The Open League like Top Eleven?</strong> Both are free
        football managers you can play on your phone. The difference is who you
        play against and where. In TOL, the clubs on your server are run by
        real people, apart from the odd bot-run club waiting for a new manager,
        and the league lives in Discord.
      </StyledParagraph>
      <StyledParagraph big>
        <strong>Do I need to buy anything to compete?</strong> No. TOL is free
        to play, and transfers are agreed between managers, not bought with
        tokens. The optional Patron extras are an extra academy spot and
        automatic substitutions for injured players.
      </StyledParagraph>
      <StyledParagraph big>
        <strong>How long does a season take?</strong> Three weeks of nightly
        league matches at 7 pm, with{' '}
        <Link to="/user-guide/competitions">cup games</Link> at noon on most
        days, then a
        three-day off-season. A Top Eleven season runs 28 days.
      </StyledParagraph>
      <StyledParagraph big>
        <strong>Can I play on my phone?</strong> Yes. TOL runs in your phone’s
        browser, so there is no app to install. It is also on Steam if you
        prefer to play on a computer.
      </StyledParagraph>
      <StyledParagraph big>
        <strong>Can I bring my Top Eleven club over?</strong> No. The two games
        are not connected. Everyone starts by claiming a club on a TOL server.
      </StyledParagraph>

      <StyledParagraph big>
        A good Top Eleven alternative keeps what you liked, which is managing a
        club from your phone, and changes what you did not. The Open League
        gives you human rivals, deals you negotiate yourself, and a three-week
        season you follow in Discord.
      </StyledParagraph>
      <StyledParagraph big>
        <Link to="/">Claim a club in The Open League</Link> and follow tonight’s
        7 pm match in Discord.
      </StyledParagraph>
    </>
  );
}
